import { createFileRoute, Link } from '@tanstack/react-router';
import { auth } from '@/lib/platform';
import { Screen, AccountButton, useCapabilities } from '@/components/chrome';
import { Icon } from '@/components/ui/icon';
import { LeadIcon } from '@/components/ui/lead-icon';
import { EmptyState } from '@/components/shared/empty-state';
import { BeeLoader } from '@/components/ui/bee-loader';
import type { Category } from '@/components/ui/category';

export const Route = createFileRoute('/_authed/')({
  component: LauncherPage,
});

interface LauncherItem {
  category: Category;
  title: string;
  description: string;
  coverage?: string;
}

// Keyed by category rather than a positional array: the call sites below index
// this per-product, and an array index is `T | undefined` under
// noUncheckedIndexedAccess (which previously went unnoticed because this app was
// never actually type-checked).
const ITEMS = {
  metro: {
    category: 'metro',
    title: 'Metro ticket',
    // Operator-neutral: this card renders before a city is chosen, so naming one
    // operator's brand here was wrong for every rider outside that city.
    description: 'Buy a metro ticket — scan the QR at the gate.',
  },
  bus: {
    category: 'bus',
    title: 'Bus ticket',
    description: 'Intra-city bus — buy a ticket and board the bus.',
  },
  ride: {
    category: 'ride',
    title: 'Book a ride',
    description: 'On-demand auto or cab — pay the driver at drop-off.',
    coverage: 'Bangalore',
  },
  pass: {
    category: 'pass',
    title: 'Entry passes',
    description: 'Events, museums & venues — book your visit and skip the queue.',
    coverage: 'Delhi',
  },
} satisfies Record<string, LauncherItem>;

function LauncherCardBody({ item }: { item: LauncherItem }) {
  return (
    <>
      <LeadIcon category={item.category} />
      <div className="wb-grow">
        <div className="wb-row wb-between wb-gap-8">
          <span className="wb-h3">{item.title}</span>
          <Icon name="chevronRight" size={20} style={{ color: 'var(--ink-3)' }} />
        </div>
        <div className="wb-small" style={{ marginTop: 2 }}>
          {item.description}
        </div>
        {item.coverage && (
          <span
            className="wb-row wb-gap-6"
            style={{ marginTop: 8, color: 'var(--ink-3)', fontSize: 12 }}
          >
            <Icon name="pin" size={13} /> {item.coverage}
          </span>
        )}
      </div>
    </>
  );
}

function LauncherPage() {
  const first = (auth.currentUser?.displayName ?? '').trim().split(' ')[0];
  const { capabilities: caps, ready } = useCapabilities();
  const anyEnabled = caps.metro || caps.bus || caps.rides || caps.passes;

  return (
    <Screen brand right={<AccountButton />}>
      <div className="wb-col wb-gap-6" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">{first ? `Hey ${first}` : 'Welcome'}</span>
        <h1 className="wb-h1">What would you like to do?</h1>
      </div>
      {!ready ? (
        <div className="wb-center" style={{ padding: '48px 0' }}>
          <BeeLoader size={48} />
        </div>
      ) : anyEnabled ? (
        <div className="wb-col wb-gap-12 wb-stagger">
          {caps.metro && (
            <Link to="/metro" className="wb-listrow cat-metro">
              <LauncherCardBody item={ITEMS.metro} />
            </Link>
          )}
          {caps.bus && (
            <Link to="/metro" search={{ mode: 'bus' }} className="wb-listrow cat-bus">
              <LauncherCardBody item={ITEMS.bus} />
            </Link>
          )}
          {caps.rides && (
            <Link to="/rides" className="wb-listrow cat-ride">
              <LauncherCardBody item={ITEMS.ride} />
            </Link>
          )}
          {caps.passes && (
            <Link to="/passes" className="wb-listrow cat-pass">
              <LauncherCardBody item={ITEMS.pass} />
            </Link>
          )}
        </div>
      ) : (
        <EmptyState
          title="Ticketing isn't available yet"
          description="Transit ticketing hasn't launched in this environment. Please check back soon."
        />
      )}
    </Screen>
  );
}
